"use client";

import * as React from "react";
import type { EmblaCarouselType } from "embla-carousel";
import type {
  UseAutoplayReturn,
  UseAutoplayProgressReturn,
  UseWarmIndicesReturn,
} from "./carousel.types";

/** Tracks autoplay playing state and exposes play/stop/toggle helpers. */
export function useAutoplay(
  emblaApi: EmblaCarouselType | undefined,
): UseAutoplayReturn {
  const [autoplayIsPlaying, setAutoplayIsPlaying] = React.useState(false);

  const onAutoplayButtonClick = React.useCallback(
    (callback: () => void) => {
      const autoplay = emblaApi?.plugins()?.autoplay;
      if (!autoplay) return;
      autoplay.stop();
      callback();
    },
    [emblaApi],
  );

  const toggleAutoplay = React.useCallback(() => {
    const autoplay = emblaApi?.plugins()?.autoplay;
    if (!autoplay) return;
    if (autoplay.isPlaying()) {
      autoplay.stop();
    } else {
      autoplay.play();
    }
  }, [emblaApi]);

  React.useEffect(() => {
    const autoplay = emblaApi?.plugins()?.autoplay;
    if (!autoplay) return;
    setAutoplayIsPlaying(autoplay.isPlaying());
    emblaApi
      .on("autoplay:play", () => setAutoplayIsPlaying(true))
      .on("autoplay:stop", () => setAutoplayIsPlaying(false))
      .on("reInit", () => setAutoplayIsPlaying(autoplay.isPlaying()));
  }, [emblaApi]);

  return { autoplayIsPlaying, toggleAutoplay, onAutoplayButtonClick };
}

/** Drives a CSS `@keyframes` animation on a ref'd element synced to the autoplay timer. */
export function useAutoplayProgress<T extends HTMLElement>(
  emblaApi: EmblaCarouselType | undefined,
  progressNode: React.RefObject<T | null>,
): UseAutoplayProgressReturn {
  const [showAutoplayProgress, setShowAutoplayProgress] = React.useState(false);
  const animationName = React.useRef("");
  const timeoutId = React.useRef(0);
  const rafId = React.useRef(0);

  const startProgress = React.useCallback(
    (timeUntilNext: number | null) => {
      const node = progressNode.current;
      if (!node || timeUntilNext === null) return;

      if (!animationName.current) {
        animationName.current = window.getComputedStyle(node).animationName;
      }

      node.style.animationName = "none";
      node.style.transform = "scaleX(0)";

      rafId.current = window.requestAnimationFrame(() => {
        timeoutId.current = window.setTimeout(() => {
          node.style.animationName = animationName.current;
          node.style.animationDuration = `${timeUntilNext}ms`;
        }, 0);
      });

      setShowAutoplayProgress(true);
    },
    [progressNode],
  );

  React.useEffect(() => {
    const autoplay = emblaApi?.plugins()?.autoplay;
    if (!autoplay) return;
    emblaApi
      .on("autoplay:timerset", () => startProgress(autoplay.timeUntilNext()))
      .on("autoplay:timerstopped", () => setShowAutoplayProgress(false));
  }, [emblaApi, startProgress]);

  React.useEffect(() => {
    return () => {
      cancelAnimationFrame(rafId.current);
      clearTimeout(timeoutId.current);
    };
  }, []);

  return { showAutoplayProgress };
}

/** Tracks which slides should eagerly load: the active slide, the next slide, and any hovered thumbnail. */
export function useWarmIndices(
  emblaApi: EmblaCarouselType | undefined,
): UseWarmIndicesReturn {
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [nextIndex, setNextIndex] = React.useState<number | null>(null);
  const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);

  React.useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => {
      const selected = emblaApi.selectedScrollSnap();
      const count = emblaApi.slideNodes().length;
      setSelectedIndex(selected);
      setNextIndex(count > 1 ? (selected + 1) % count : null);
    };
    emblaApi.on("select", onSelect).on("reInit", onSelect);
    onSelect();
    return () => {
      emblaApi.off("select", onSelect).off("reInit", onSelect);
    };
  }, [emblaApi]);

  const warmIndices = React.useMemo(() => {
    const set = new Set<number>([selectedIndex]);
    if (nextIndex !== null) set.add(nextIndex);
    if (hoveredIndex !== null) set.add(hoveredIndex);
    return set;
  }, [selectedIndex, nextIndex, hoveredIndex]);

  return { warmIndices, setHoveredIndex };
}
